import type { IconAssets } from "#types/assets.ts";
import type { ConnectedIntegration, ConnectorChannelType, ConnectorType, DataSource } from "#types/types.ts";
import { asanaManifest } from "./asana.tsx";
import { atlassianManifest } from "./atlassian.tsx";
import { clickupManifest } from "./clickup.tsx";
import { confluenceManifest } from "./confluence.tsx";
import { figmaManifest } from "./figma.tsx";
import { githubManifest } from "./github.tsx";
import { gongManifest } from "./gong.tsx";
import { googleManifest } from "./google.tsx";
import { granolaManifest } from "./granola.tsx";
import { hubspotManifest } from "./hubspot.tsx";
import { intercomManifest } from "./intercom.tsx";
import { jiraManifest } from "./jira.tsx";
import { linearManifest } from "./linear.tsx";
import { miroManifest } from "./miro.tsx";
import { mixpanelManifest } from "./mixpanel.tsx";
import { notionManifest } from "./notion.tsx";
import { outlookManifest } from "./outlook.tsx";
import { sharepointManifest } from "./sharepoint.tsx";
import { slackManifest } from "./slack.tsx";
import { teamsManifest } from "./teams.tsx";
import { zoomManifest } from "./zoom.tsx";
import type { IntegrationManifest } from "./types.ts";

/**
 * The exhaustiveness spine. `Exclude<ConnectedIntegration, "unknown">` fails `tsc` the instant a real
 * `ConnectedIntegration` member has no manifest here. Adding a third-party integration means adding
 * one file plus one line — no other registry, switch, or card to touch.
 */
export const INTEGRATION_MANIFESTS: Record<Exclude<ConnectedIntegration, "unknown">, IntegrationManifest> = {
    jira: jiraManifest,
    slack: slackManifest,
    confluence: confluenceManifest,
    notion: notionManifest,
    outlook: outlookManifest,
    teams: teamsManifest,
    google: googleManifest,
    sharepoint: sharepointManifest,
    granola: granolaManifest,
    hubspot: hubspotManifest,
    asana: asanaManifest,
    clickup: clickupManifest,
    linear: linearManifest,
    miro: miroManifest,
    intercom: intercomManifest,
    mixpanel: mixpanelManifest,
    gong: gongManifest,
    figma: figmaManifest,
    github: githubManifest,
    atlassian: atlassianManifest,
    zoom: zoomManifest
};

/**
 * Safe lookup for a `ConnectedIntegration` that arrived off the wire, where the exhaustiveness
 * guarantee above doesn't hold: the backend can emit a value newer than this build's manifests
 * (e.g. a LaunchDarkly flag flip ahead of a frontend deploy). Returns `undefined` for `"unknown"`
 * and for any key with no manifest yet, instead of letting the caller index `INTEGRATION_MANIFESTS`
 * directly and risk destructuring `undefined`.
 */
export const getIntegrationManifest = (key: ConnectedIntegration): IntegrationManifest | undefined =>
    key === "unknown" ? undefined : INTEGRATION_MANIFESTS[key];

const ALL_MANIFESTS: IntegrationManifest[] = Object.values(INTEGRATION_MANIFESTS);

/** Reverse lookup the cards key off: both receive a `ConnectorChannelType` from the API, never a `ConnectedIntegration`. */
export const channelTypeToIntegration: Partial<Record<ConnectorChannelType, IntegrationManifest>> =
    Object.fromEntries(ALL_MANIFESTS.map(manifest => [manifest.channelType, manifest]));

/** Reverse lookup for citations: one `DataSource` resolves to the integration that owns it. */
export const dataSourceToIntegration: Partial<Record<DataSource, IntegrationManifest>> =
    Object.fromEntries(ALL_MANIFESTS.flatMap(manifest => manifest.dataSources.map(dataSource => [dataSource, manifest])));

export interface PersonalChannel {
    nameKey: string;
    iconUrl: IconAssets;
    iconUrlAlt: IconAssets;
    iconUrlDark?: IconAssets;
    iconUrlAltDark?: IconAssets;
    connectorType?: ConnectorType;
}

/** The personal slice of `channels` (types/channels.tsx), derived from the manifests instead of hand-authored. */
export const personalChannels: Partial<Record<ConnectorChannelType, PersonalChannel>> =
    Object.fromEntries(
        ALL_MANIFESTS.map(manifest => [
            manifest.channelType,
            { nameKey: manifest.nameKey, iconUrl: manifest.iconUrl, iconUrlAlt: manifest.iconUrlAlt, iconUrlDark: manifest.iconUrlDark, iconUrlAltDark: manifest.iconUrlAltDark, connectorType: manifest.connectorType }
        ])
    );

export type { IntegrationManifest } from "./types.ts";
